import {t} from "@lingui/core/macro";
import * as React from "react";

import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {
    ActivityLevelCell,
    ActivityNameCell,
    ActivityScoreCell,
    DateCell,
    DefaultNotApplicable,
    MultipleSkillChangesModal,
    MultipleSkillLevelChangeIcon,
    type GQLActivitySessionV2 as ActivitySessionsReportRow,
} from "@khan/activity-sessions-query";
import {Column} from "@khan/component-table";

import ReportTable from "../report-table.tsx";

type Props = {
    data: Array<ActivitySessionsReportRow>;
    studentKaid: string;
};

type State = {
    // Data that is displayed in the most recently opened multiple skill
    // changes modal.
    multipleSkillChangesModalData: ActivitySessionsReportRow | null | undefined;
};

class ActivitySessionsTable extends React.Component<Props, State> {
    state: State = {
        multipleSkillChangesModalData: null,
    };

    renderTable: () => React.ReactNode = () => (
        <ReportTable {...this.props}>
            <Column id="name" label={t`Activity`} maxWidth={300}>
                {(row: ActivitySessionsReportRow) => (
                    <ActivityNameCell
                        row={row}
                        studentKaid={this.props.studentKaid}
                    />
                )}
            </Column>
            <Column id="date" label={t`Date`}>
                {(row: ActivitySessionsReportRow) => <DateCell row={row} />}
            </Column>
            <Column id="level" label={t`Level`}>
                {(row: ActivitySessionsReportRow) => (
                    <ActivityLevelCell
                        activitySessionsReportRow={row}
                        onShowDetails={(row: ActivitySessionsReportRow) =>
                            this.setState({
                                multipleSkillChangesModalData: row,
                            })
                        }
                    />
                )}
            </Column>
            <Column id="change" align="center" label={t`Change`}>
                {(row: ActivitySessionsReportRow) => (
                    <MultipleSkillLevelChangeIcon activitySession={row} />
                )}
            </Column>
            <Column
                id="score"
                align="right"
                label={t`Correct/Total Problems`}
                tooltipText={t`The number of problems the student got correct, and the total number of problems in the activity.`}
            >
                {(row: ActivitySessionsReportRow) => (
                    <ActivityScoreCell row={row} />
                )}
            </Column>
            <Column id="duration" align="right" label={t`Time (min)`}>
                {(row: ActivitySessionsReportRow) =>
                    row.durationMinutes != null ? (
                        <Body>{row.durationMinutes}</Body>
                    ) : (
                        <DefaultNotApplicable />
                    )
                }
            </Column>
        </ReportTable>
    );

    render(): React.ReactNode {
        return (
            <React.Fragment>
                {this.renderTable()}
                {this.state.multipleSkillChangesModalData && (
                    <ModalLauncher
                        opened={true}
                        onClose={() =>
                            this.setState({
                                multipleSkillChangesModalData: null,
                            })
                        }
                        modal={
                            <MultipleSkillChangesModal
                                activitySessionsReportRow={
                                    this.state.multipleSkillChangesModalData
                                }
                            />
                        }
                    />
                )}
            </React.Fragment>
        );
    }
}

export default ActivitySessionsTable;
